import streamService from '@/common/api/stream.service';
import { useAuthStore } from '@/stores/auth';
import { ref } from 'vue';

/**
 * The one client for Lens AI capabilities (#6393). Streams a turn from
 * POST /api/account/{account}/lens/run/{capabilityId} and exposes its events as refs.
 *
 * The server owns conversation history: send() posts the conversation id and the new message only,
 * never earlier messages. Proposals are only marked here — committing one is the caller's job,
 * through the feature's existing services.
 *
 * @param {string} capabilityId e.g. 'help.ask'
 * @param {{ onEvent?: (event: object) => void }} [options] onEvent sees every streamed event, after the refs update
 */
export function useLens(capabilityId, { onEvent } = {}) {
    const authStore = useAuthStore();

    const text = ref('');
    const status = ref(null);
    const proposals = ref([]);
    const result = ref(null);
    const error = ref(null);
    const running = ref(false);
    const conversationId = ref(null);

    let controller = null;
    let lastMessage = null;
    let lastOpts = {};

    function handleEvent(event) {
        switch (event?.type) {
            case 'status':
                status.value = event.message ?? event.phase ?? null;
                break;
            case 'text':
                text.value += event.delta ?? '';
                break;
            case 'proposal':
                if (event.proposal) proposals.value.push({ ...event.proposal, state: 'pending' });
                break;
            case 'result':
                result.value = event.result ?? null;
                break;
            case 'error':
                error.value = { message: event.error?.message ?? 'Something went wrong.', code: event.error?.code ?? 'error' };
                break;
            case 'done':
                if (event.conversationId) conversationId.value = event.conversationId;
                status.value = null;
                running.value = false;
                break;
            default:
                break;
        }
        onEvent?.(event);
    }

    function onLine(line) {
        if (!line || !line.trim()) return;
        let event;
        try {
            event = JSON.parse(line);
        } catch (e) {
            // A partial line is completed by the next chunk; anything else is not ours to parse.
            if (e instanceof SyntaxError) return;
            throw e;
        }
        handleEvent(event);
    }

    /** The {message, code} of a refusal returned before streaming started, from stream.service's error. */
    function errorFrom(err) {
        const body = String(err?.message ?? '').match(/^HTTP error! status: \d+ - ([\s\S]*)$/)?.[1];
        if (body) {
            try {
                const parsed = JSON.parse(body);
                if (parsed?.error) {
                    return { message: parsed.error.message ?? 'The request was refused.', code: parsed.error.code ?? 'error' };
                }
            } catch {
                // Not JSON: fall through to the generic message.
            }
        }
        return { message: 'The AI service could not be reached. Please try again.', code: 'network_error' };
    }

    /**
     * @param {string} message
     * @param {{ page?: {routeName: string, label: string}, attachments?: Array, context?: object, purpose?: string }} [opts]
     */
    async function send(message, opts = {}) {
        if (running.value) stop();

        lastMessage = message;
        lastOpts = opts;
        text.value = '';
        status.value = null;
        proposals.value = [];
        result.value = null;
        error.value = null;
        running.value = true;

        const thisController = new AbortController();
        controller = thisController;

        try {
            await streamService.streamPost(`/api/account/${authStore.account.id}/lens/run/${capabilityId}`, { conversationId: conversationId.value, message, ...opts }, onLine, {
                signal: thisController.signal,
            });
        } catch (err) {
            if (!thisController.signal.aborted) {
                console.error('Lens request failed', { capabilityId, conversationId: conversationId.value, error: err });
                error.value = errorFrom(err);
            }
        } finally {
            if (controller === thisController) {
                running.value = false;
                controller = null;
            }
        }
    }

    /** Aborts the request; the server notices the disconnect and ends the turn. */
    function stop() {
        controller?.abort();
        controller = null;
        running.value = false;
    }

    /** Resends the last message in the same conversation. */
    function retry() {
        if (lastMessage === null) return Promise.resolve();
        return send(lastMessage, lastOpts);
    }

    function markProposal(id, state) {
        const proposal = proposals.value.find((p) => p.id === id);
        if (!proposal) return null;
        proposal.state = state;
        return proposal;
    }

    const acceptProposal = (id) => markProposal(id, 'accepted');
    const rejectProposal = (id) => markProposal(id, 'rejected');

    return {
        send,
        stop,
        retry,
        text,
        status,
        proposals,
        result,
        error,
        running,
        conversationId,
        acceptProposal,
        rejectProposal,
    };
}
